import React, { useEffect, useState } from "react";

import { ChevronRight, Loader2, Wallet, Plus, Info } from "lucide-react";

import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Progress } from "../../../components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip";
import { useAuth } from "../../../contexts/AuthContext";
import { usePermissions } from "../../../hooks/usePermissions";
import {
  cloudUsageApi,
  CloudBalanceWithUsage,
  formatBytes,
  formatNumber,
} from "../../../lib/api/cloud-usage";
import { cn } from "../../../lib/utils";

interface CloudBalanceCardProps {
  className?: string;
  onAddFunds: () => void;
}

// Meter display names
// Note: db_storage and file_storage removed - Supabase doesn't charge separately
const METER_LABELS: Record<string, string> = {
  edge_functions: "Edge Functions",
  r2_storage: "Assets Storage",
  app_traffic: "App Traffic",
  realtime_messages: "Realtime Messages",
  realtime_peak_connections: "Realtime Connections",
  instance: "Database Compute",
};

// Meter tier sizes for display
const METER_TIER_INFO: Record<string, { size: number; unit: string }> = {
  edge_functions: { size: 1000000, unit: "invocations" },
  r2_storage: { size: 1, unit: "GB" },
  app_traffic: { size: 1000000, unit: "requests" },
  realtime_messages: { size: 1000000, unit: "messages" },
  realtime_peak_connections: { size: 1000, unit: "connections" },
  instance: { size: 1, unit: "projects" },
};

interface MeterRowProps {
  meter: string;
  usage: number;
  tiersPurchased: number;
  cost: number;
  included?: number;
}

const MeterRow: React.FC<MeterRowProps> = ({ meter, usage, tiersPurchased, cost, included }) => {
  const [open, setOpen] = useState(false);
  const label = METER_LABELS[meter] || meter;
  const tierInfo = METER_TIER_INFO[meter];

  // For instance meter, capacity includes both included + purchased tiers
  const isInstanceMeter = meter === "instance";
  const totalCapacity = isInstanceMeter && included
    ? included + tiersPurchased
    : tiersPurchased * (tierInfo?.size || 1);
  const usagePercent = totalCapacity > 0 ? Math.min((usage / totalCapacity) * 100, 100) : 0;

  // Format usage display
  const formatUsage = (value: number): string => {
    if (meter.includes("storage")) {
      return formatBytes(value * 1024 * 1024 * 1024); // Convert GB to bytes for display
    }
    return formatNumber(value);
  };

  // Format capacity display
  const formatCapacity = (): string => {
    if (!tierInfo) return "";
    if (isInstanceMeter && included) {
      return `${totalCapacity} ${tierInfo.unit}`;
    }
    if (meter.includes("storage")) {
      return `${tiersPurchased} ${tierInfo.unit}`;
    }
    return `${formatNumber(tiersPurchased * tierInfo.size)} ${tierInfo.unit}`;
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between py-2.5 px-1 hover:bg-muted/50 rounded-md transition-colors cursor-pointer">
        <div className="flex items-center gap-1.5">
          <ChevronRight
            className={cn(
              "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
              open && "rotate-90"
            )}
          />
          <span className="text-sm">{label}</span>
          {meter === "r2_storage" && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Workspace-level storage (not per-project)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <span className="text-sm font-medium tabular-nums">
          ${cost.toFixed(2)}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-5 py-2 pl-2 space-y-2">
          {/* Progress bar */}
          <div className="space-y-1">
            <Progress value={usagePercent} className="h-1.5 bg-foreground/20 [&>div]:bg-foreground" />
            <div className="flex justify-between text-xs text-muted-foreground">
              {isInstanceMeter && included ? (
                <>
                  <span>{usage} active / {included} included</span>
                  {totalCapacity > 0 && <span>{formatCapacity()} capacity</span>}
                </>
              ) : (
                <>
                  <span>{formatUsage(usage)} used</span>
                  {tiersPurchased > 0 && <span>{formatCapacity()} capacity</span>}
                </>
              )}
            </div>
          </div>
          {tiersPurchased > 0 && (
            <p className="text-xs text-muted-foreground">
              {tiersPurchased} tier{tiersPurchased > 1 ? "s" : ""} purchased this cycle
            </p>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export const CloudBalanceCard: React.FC<CloudBalanceCardProps> = ({
  className,
  onAddFunds,
}) => {
  const { getIdToken } = useAuth();
  const { isAdminOrOwner } = usePermissions();
  const [data, setData] = useState<CloudBalanceWithUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBalance();
  }, []);

  const loadBalance = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) {
        setError("Authentication required");
        return;
      }
      const result = await cloudUsageApi.getBalanceWithUsage(token);
      setData(result);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
      console.error("Error loading cloud balance:", err);
      if (axiosErr.response?.status !== 404) {
        setError(axiosErr.response?.data?.message || "Failed to load balance data");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="py-4">
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className={className}>
        <CardContent className="py-4">
          <p className="text-sm text-muted-foreground">
            No balance data available.
          </p>
        </CardContent>
      </Card>
    );
  }

  const { balance, costs, totalCost } = data;
  const planUsedPercent = balance.allocated > 0
    ? Math.min(((balance.allocated - balance.planBalance) / balance.allocated) * 100, 100)
    : 0;

  return (
    <Card className={className}>
      <CardContent className="py-4 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Wallet className="h-4 w-4" />
            <span>Cloud Balance</span>
          </div>
          <span className="text-xs text-muted-foreground">
            Resets 1st of each month
          </span>
        </div>

        {/* Plan Allowance */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground">Plan Allowance</span>
              {balance.billingSource === 'embedded' && (
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">
                        Allocated based on {balance.screenCount} screen{balance.screenCount !== 1 ? 's' : ''} with OptiDev Add-on
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <span className="font-medium tabular-nums">
              ${balance.planBalance.toFixed(2)} / ${balance.allocated.toFixed(2)}
            </span>
          </div>
          <Progress value={100 - planUsedPercent} className="h-2 bg-foreground/20 [&>div]:bg-foreground" />
        </div>

        {/* Added Funds */}
        <div className="flex items-center justify-between py-2 border-t border-border/50">
          <div>
            <p className="text-sm text-muted-foreground">Added Funds</p>
            <p className="text-xs text-muted-foreground">(never resets)</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium tabular-nums">
              ${balance.topupBalance.toFixed(2)}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={onAddFunds}
              disabled={!isAdminOrOwner}
              className="h-7 px-2"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add
            </Button>
          </div>
        </div>

        {/* Total Available */}
        <div className="flex items-center justify-between py-2 border-t border-border/50 bg-muted/30 -mx-4 px-4 rounded-md">
          <span className="text-sm font-medium">Total Available</span>
          <span className="text-lg font-semibold tabular-nums">
            ${balance.totalBalance.toFixed(2)}
          </span>
        </div>

        {/* Usage Breakdown by Meter */}
        {costs.length > 0 && (
          <div className="space-y-1 pt-2 border-t border-border/50">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
              <span>Usage This Cycle</span>
              <span className="font-medium">${totalCost.toFixed(2)}</span>
            </div>
            {costs.map((meterCost) => (
              <MeterRow
                key={meterCost.meter}
                meter={meterCost.meter}
                usage={meterCost.usage}
                tiersPurchased={meterCost.tiersPurchased}
                cost={meterCost.cost}
                included={meterCost.included}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
